import React, { useEffect, useMemo, useState } from 'react'
import { Dialog, DialogActions, DialogContent, IconButton } from '@mui/material'
import { Plus, Trash2, X } from 'lucide-react'
import Button from '../ui/Button'
import { showConfirmModal } from '../ui/ConfirmDialog'
import Input from '../ui/Input'
import Select from '../ui/Select'
import type { CodeItemAttributeDefinition, CodeItemAttributeSchemaScope, CodeItemGroup } from '../../types/codeIndex'
import {
  getPopulatedAttributeCount,
  normalizeAttributeName,
  normalizeAttributeSchemaScopes,
  normalizeAttributeType,
  validateAttributeSchemaScopes,
} from '../../utils/codeItemAttributes'
import { normalizeRowGroupName } from '../../utils/codeIndexUtils'

type AttributeSchemaDialogProps = {
  open: boolean
  group: CodeItemGroup | null
  rowGroupNames: string[]
  onClose: () => void
  onSave: (schemas: CodeItemAttributeSchemaScope[]) => void
}

type AttributeDraft = {
  id: string
  name: string
  type: CodeItemAttributeDefinition['type']
  unit: string
  allowedValuesInput: string
}

function createAttributeId(): string {
  return `attr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function schemaKey(scope: 'codeItem' | 'rowGroup', group: string | null): string {
  return scope === 'rowGroup'
    ? `rowGroup:${normalizeRowGroupName(group)?.toLowerCase() || ''}`
    : 'codeItem'
}

function attributeToDraft(attribute: CodeItemAttributeDefinition): AttributeDraft {
  return {
    id: attribute.id,
    name: attribute.name,
    type: normalizeAttributeType(attribute.type),
    unit: attribute.unit || '',
    allowedValuesInput: (attribute.allowedValues || []).join(', '),
  }
}

function parseAllowedValues(value: string): string[] {
  return value
    .split(/[,\n]+/)
    .map(item => item.trim())
    .filter(Boolean)
}

function draftToAttribute(draft: AttributeDraft): CodeItemAttributeDefinition | null {
  const name = normalizeAttributeName(draft.name)
  if (!name) return null
  const type = normalizeAttributeType(draft.type)

  return {
    id: draft.id || createAttributeId(),
    name,
    type,
    unit: draft.unit.trim() || null,
    allowedValues: type === 'enumerated'
      ? parseAllowedValues(draft.allowedValuesInput)
      : [],
  }
}

function buildDraftSchemas(schemas: CodeItemAttributeSchemaScope[]): CodeItemAttributeSchemaScope[] {
  return normalizeAttributeSchemaScopes(schemas).map(schema => ({
    ...schema,
    attributes: schema.attributes.map(attribute => ({ ...attribute })),
  }))
}

const ATTRIBUTE_TABLE_GRID_CLASS = 'grid grid-cols-[minmax(0,1.1fr)_minmax(100px,0.8fr)_minmax(0,0.7fr)_32px] gap-2'
const ATTRIBUTE_TABLE_WITH_ALLOWED_VALUES_GRID_CLASS = 'grid grid-cols-[minmax(0,1.1fr)_minmax(100px,0.8fr)_minmax(0,0.7fr)_minmax(0,1fr)_32px] gap-2'
const NEW_ATTRIBUTE_GRID_CLASS = 'grid grid-cols-[minmax(0,1fr)_minmax(100px,0.75fr)_minmax(0,0.75fr)_auto] gap-2'

export const AttributeSchemaDialog: React.FC<AttributeSchemaDialogProps> = ({
  open,
  group,
  rowGroupNames,
  onClose,
  onSave,
}) => {
  const [draftSchemas, setDraftSchemas] = useState<CodeItemAttributeSchemaScope[]>([])
  const [activeScopeKey, setActiveScopeKey] = useState('codeItem')
  const [newAttribute, setNewAttribute] = useState<AttributeDraft>({
    id: '',
    name: '',
    type: 'numeric',
    unit: '',
    allowedValuesInput: '',
  })
  const [allowedValuesInputs, setAllowedValuesInputs] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    const nextSchemas = buildDraftSchemas(group?.attributeSchemas || [])
    setDraftSchemas(nextSchemas)
    setAllowedValuesInputs(nextSchemas.reduce<Record<string, string>>((inputs, schema) => {
      schema.attributes.forEach(attribute => {
        inputs[attribute.id] = (attribute.allowedValues || []).join(', ')
      })
      return inputs
    }, {}))
    setActiveScopeKey('codeItem')
    setNewAttribute({ id: '', name: '', type: 'numeric', unit: '', allowedValuesInput: '' })
    setError(null)
  }, [group, open])

  const scopeOptions = useMemo(() => {
    const normalizedRowGroups = Array.from(new Set(
      rowGroupNames.map(name => normalizeRowGroupName(name)).filter((name): name is string => Boolean(name))
    ))

    return [
      { key: 'codeItem', label: 'Code Item', scope: 'codeItem' as const, group: null },
      ...normalizedRowGroups.map(name => ({
        key: schemaKey('rowGroup', name),
        label: name,
        scope: 'rowGroup' as const,
        group: name,
      }))
    ]
  }, [rowGroupNames])

  const activeScope = scopeOptions.find(option => option.key === activeScopeKey) || scopeOptions[0]
  const activeSchema = draftSchemas.find(schema => schemaKey(schema.scope, schema.group || null) === activeScope.key)
  const activeAttributes = activeSchema?.attributes || []
  const showAllowedValuesColumn = activeAttributes.some(attribute => attribute.type === 'enumerated')
  const attributeTableGridClass = showAllowedValuesColumn
    ? ATTRIBUTE_TABLE_WITH_ALLOWED_VALUES_GRID_CLASS
    : ATTRIBUTE_TABLE_GRID_CLASS
  const canAddAttribute = Boolean(
    newAttribute.name.trim() &&
    (
      newAttribute.type !== 'enumerated' ||
      parseAllowedValues(newAttribute.allowedValuesInput).length > 0
    )
  )

  const updateActiveAttributes = (updater: (current: CodeItemAttributeDefinition[]) => CodeItemAttributeDefinition[]) => {
    if (!activeScope) return

    setDraftSchemas(currentSchemas => {
      const currentIndex = currentSchemas.findIndex(schema => schemaKey(schema.scope, schema.group || null) === activeScope.key)
      const currentAttributes = currentIndex >= 0 ? currentSchemas[currentIndex].attributes : []
      const nextAttributes = updater(currentAttributes)

      if (nextAttributes.length === 0) {
        return currentSchemas.filter((_, index) => index !== currentIndex)
      }

      const nextSchema: CodeItemAttributeSchemaScope = {
        scope: activeScope.scope,
        group: activeScope.group,
        attributes: nextAttributes,
      }

      if (currentIndex === -1) {
        return [...currentSchemas, nextSchema]
      }

      return currentSchemas.map((schema, index) => index === currentIndex ? nextSchema : schema)
    })
  }

  const handleAddAttribute = () => {
    const attribute = draftToAttribute({ ...newAttribute, id: createAttributeId() })
    if (!attribute) {
      setError('Attribute name is required.')
      return
    }

    if (attribute.type === 'enumerated' && (!attribute.allowedValues || attribute.allowedValues.length === 0)) {
      setError('Enumerated attributes need at least one allowed value.')
      return
    }

    const duplicate = activeAttributes.some(existing => existing.name.trim().toLowerCase() === attribute.name.trim().toLowerCase())
    if (duplicate) {
      setError(`Attribute "${attribute.name}" already exists in this scope.`)
      return
    }

    updateActiveAttributes(current => [...current, attribute])
    setNewAttribute({ id: '', name: '', type: 'numeric', unit: '', allowedValuesInput: '' })
    setError(null)
  }

  const handleUpdateAttribute = (attributeId: string, patch: Partial<AttributeDraft>) => {
    if (patch.allowedValuesInput !== undefined) {
      setAllowedValuesInputs(current => ({
        ...current,
        [attributeId]: patch.allowedValuesInput || '',
      }))
    } else if (patch.type && normalizeAttributeType(patch.type) !== 'enumerated') {
      setAllowedValuesInputs(current => ({
        ...current,
        [attributeId]: '',
      }))
    }

    updateActiveAttributes(current => current.map(attribute => {
      if (attribute.id !== attributeId) return attribute

      const draft = {
        ...attributeToDraft(attribute),
        ...patch,
      }
      return draftToAttribute(draft) || attribute
    }))
    setError(null)
  }

  const handleDeleteAttribute = async (attribute: CodeItemAttributeDefinition) => {
    const populatedCount = getPopulatedAttributeCount(group?.rows || [], attribute.id)
    if (populatedCount > 0) {
      const confirmed = await showConfirmModal({
        title: 'Remove Attribute',
        description: `Removing ${attribute.name} will delete populated data from ${populatedCount} value${populatedCount === 1 ? '' : 's'}. Do you want to continue?`,
        confirmText: 'Remove',
        cancelText: 'Cancel',
      })

      if (!confirmed) return
    }

    updateActiveAttributes(current => current.filter(item => item.id !== attribute.id))
    setAllowedValuesInputs(current => {
      const { [attribute.id]: _removed, ...remaining } = current
      return remaining
    })
  }

  const handleSave = () => {
    const normalizedSchemas = normalizeAttributeSchemaScopes(draftSchemas)
    const validationError = validateAttributeSchemaScopes(normalizedSchemas)
    if (validationError) {
      setError(validationError)
      return
    }

    onSave(normalizedSchemas)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '8px' } }}
    >
      <div className="flex items-start justify-between border-b border-[#E2E8F0] px-5 py-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Configure Attributes</h2>
          <p className="text-xs text-gray-500">{group ? `Code Item ${group.codeItemPos.code} - ${group.title || 'Untitled'}` : ''}</p>
        </div>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <X className="h-4 w-4" />
        </IconButton>
      </div>

      <DialogContent sx={{ padding: '16px 20px 8px' }}>
        <div className="space-y-4">
          {scopeOptions.length > 1 && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-700">Scope</label>
              <Select
                value={activeScopeKey}
                onChange={(event) => setActiveScopeKey(event.target.value)}
                selectSize="md"
                aria-label="Attribute scope"
              >
                {scopeOptions.map(option => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
          )}

          {activeAttributes.length > 0 && (
            <div className="overflow-hidden rounded border border-[#D6E4F2]">
              <div className={`${attributeTableGridClass} bg-[#F6F8FA] px-3 py-2 text-xs font-semibold text-gray-600`}>
                <span>Name</span>
                <span>Type</span>
                <span>Unit</span>
                {showAllowedValuesColumn && <span>Allowed values</span>}
                <span />
              </div>
              {activeAttributes.map(attribute => (
                <div key={attribute.id} className={`${attributeTableGridClass} items-center border-t border-[#E2E8F0] px-3 py-2`}>
                  <Input
                    value={attribute.name}
                    onChange={(event) => handleUpdateAttribute(attribute.id, { name: event.target.value })}
                    aria-label={`Name for ${attribute.name}`}
                  />
                  <Select
                    value={attribute.type}
                    onChange={(event) => handleUpdateAttribute(attribute.id, { type: normalizeAttributeType(event.target.value) })}
                    aria-label={`Type for ${attribute.name}`}
                  >
                    <option value="numeric">Numeric</option>
                    <option value="text">Text</option>
                    <option value="enumerated">Enumerate</option>
                  </Select>
                  <Input
                    value={attribute.unit || ''}
                    onChange={(event) => handleUpdateAttribute(attribute.id, { unit: event.target.value })}
                    aria-label={`Unit for ${attribute.name}`}
                  />
                  {showAllowedValuesColumn && (
                    attribute.type === 'enumerated' ? (
                      <Input
                        value={allowedValuesInputs[attribute.id] ?? (attribute.allowedValues || []).join(', ')}
                        onChange={(event) => handleUpdateAttribute(attribute.id, { allowedValuesInput: event.target.value })}
                        placeholder="Value 1, Value 2"
                        aria-label={`Allowed values for ${attribute.name}`}
                      />
                    ) : (
                      <span />
                    )
                  )}
                  <button
                    type="button"
                    onClick={() => { void handleDeleteAttribute(attribute) }}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-gray-500 hover:bg-[#FFF5F5] hover:text-[#B3261E]"
                    aria-label={`Remove ${attribute.name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="rounded border border-[#B7D4F4] bg-[#F5FAFF] p-3">
            <div className="mb-2 text-xs font-semibold text-[#0D47A1]">Add new attribute</div>
            <div className={NEW_ATTRIBUTE_GRID_CLASS}>
              <Input
                value={newAttribute.name}
                onChange={(event) => setNewAttribute(current => ({
                  ...current,
                  name: normalizeAttributeName(event.target.value),
                }))}
                placeholder="Name"
              />
              <Select
                value={newAttribute.type}
                onChange={(event) => setNewAttribute(current => ({ ...current, type: normalizeAttributeType(event.target.value) }))}
                aria-label="Type for new attribute"
              >
                <option value="numeric">Numeric</option>
                <option value="text">Text</option>
                <option value="enumerated">Enumerate</option>
              </Select>
              <Input
                value={newAttribute.unit}
                onChange={(event) => setNewAttribute(current => ({ ...current, unit: event.target.value }))}
                placeholder="Unit (optional)"
              />
              <Button
                type="button"
                variant="primary"
                size="sm"
                className="shrink-0 whitespace-nowrap"
                onClick={handleAddAttribute}
                disabled={!canAddAttribute}
              >
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>
            {newAttribute.type === 'enumerated' && (
              <Input
                value={newAttribute.allowedValuesInput}
                onChange={(event) => setNewAttribute(current => ({ ...current, allowedValuesInput: event.target.value }))}
                placeholder="Value 1, Value 2"
                aria-label="Allowed values for new attribute"
                className="mt-2"
              />
            )}
          </div>

          {error && (
            <div className="rounded border border-[#F5C2C7] bg-[#FFF5F5] px-3 py-2 text-sm text-[#B3261E]" role="alert">
              {error}
            </div>
          )}
        </div>
      </DialogContent>

      <DialogActions sx={{ padding: '12px 20px 16px' }}>
        <Button type="button" variant="secondary" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" variant="primary" size="sm" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AttributeSchemaDialog
